import * as Ably from 'ably';
import { ChatClient, AllFeaturesEnabled, LogLevel } from '@ably/chat';
import { showNotification } from '@/api/services/notificationServices';
import { setInboxCount, setMentionsCount, setUnassignedCount, setUnassignedData, setInboxData, setMentionsData } from '@/store/slices/notificationSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { NotificationState, NotificationCounts } from '@/store/slices/notificationSlice';
import { fetchUnassignedTickets, setNewResponseTicket } from '@/store/slices/inboxSlice';
import { fetchTickets } from '@/store/slices/inboxSlice';
import { clearTickets } from '@/store/slices/inboxSlice';
import { HttpClient } from '@/api/services/http';
import { addNewTicket } from '@/store/slices/tickets/ticketsSlice';
import { addNewTicketToViews } from '@/store/slices/ticketPages/ticketPagesSlice';
let realtimeClient: Ably.Realtime | null = null;
let clientInitialized = false;
let chatInstance: ChatClient | null = null;

// Track active subscriptions for cleanup
let activeSubscriptions: Array<{ channel: string; subscription: any }> = [];
// Safely retrieve the API key from environment variables
// const API_KEY = "4_E1dA.YqAmpw:9x08tfx_NuT8bxyAISvAv34zGJ4nUzaxrviEhczNnz4";

/**
 * Initialize the Ably client (Legacy)
 */
// export const initializeAbly = (): Ably.Realtime => {
//   if (!clientInitialized) {
//     if (!API_KEY) {
//       console.error('Ably API key is missing. Please check your environment variables.');
//       return createMockClient();
//     }

//     try {

//       // Initialize with API key or fallback to options
//       realtimeClient = new Ably.Realtime({
//         key: API_KEY,
//         clientId: 'agent-' + Math.random().toString(36).substring(2, 15),
//         echoMessages: false,
//       });

//       // Add event listeners
//       realtimeClient.connection.on('connected', () => {
//       });

//       realtimeClient.connection.on('disconnected', () => {
//       });

//       realtimeClient.connection.on('failed', (err) => {
//         console.error('Ably connection failed:', err);
//       });

//       clientInitialized = true;
//     } catch (error) {
//       console.error('Failed to initialize Ably client:', error);
//       return createMockClient();
//     }
//   }

//   return realtimeClient || createMockClient();
// };

/**
 * Create a mock Ably client for development/testing
 */
const createMockClient = () => {
  // This is a simple mock object implementing the minimal Ably interface we need
  return {
    connection: {
      state: 'connected',
      on: (_event: string, _callback: Function) => ({})
    },
    channels: {
      get: (channelName: string) => ({
        name: channelName,
        subscribe: (_eventName: string, callback: Function) => {
          return {
            unsubscribe: () => console.log(`Mock unsubscribe from ${channelName}`)
          };
        },
        presence: {
          enter: (data: any) => console.log(`Mock enter presence on ${channelName}`, data),
          leave: () => console.log(`Mock leave presence on ${channelName}`),
          subscribe: (_event: string, callback: Function) => ({
            unsubscribe: () => console.log(`Mock unsubscribe from presence on ${channelName}`)
          }),
          get: () => Promise.resolve([])
        },
        publish: (_eventName: string, data: any) =>
          Promise.resolve(console.log(`Mock publish to ${channelName}`, data))
      })
    },
    close: () => console.log('Mock Ably client closed'),
  } as unknown as Ably.Realtime;
};

/**
 * Get a channel for a specific ticket (Legacy)
 */
export const getTicketChannel = async (ticketId: string) => {
  const client = await initAbly();
  return client.channels.get(`ticket:${ticketId}`);
};

/**
 * Create a channel name for a workspace (Legacy)
 */
export const getWorkspaceChannelName = (workspaceId: string) => {
  return `workspace:${workspaceId}`;
};

/**
 * Get a workspace channel (Legacy)
 */
export const getWorkspaceChannel = async (workspaceId: string) => {
  const client = await initAbly();
  const channelName = getWorkspaceChannelName(workspaceId);
  return client.channels.get(channelName);
};

/**
 * Cleanup Ably resources (Legacy)
 */
export const cleanupAbly = () => {
  // Clean up all active subscriptions
  activeSubscriptions.forEach(({ subscription }) => {
    try {
      subscription.unsubscribe();
    } catch (error) {
      console.warn('Error unsubscribing from Ably channel:', error);
    }
  });
  activeSubscriptions = [];

  if (realtimeClient) {
    realtimeClient.close();
    realtimeClient = null;
    clientInitialized = false;
  }

  if (ably) {
    ably.close();
    ably = null;
  }

  if (chatInstance) {
    chatInstance.close();
    chatInstance = null;
  }
};

// New Ably Client Implementation

let ably: any = null;

/**
 * Initialize Ably with modern approach
 */
export const initAbly = async () => {
  if (ably) return ably;

  try {
    console.log('Initializing Ably with token authentication');

    // Get workspace ID for the header
    const workspaceId = localStorage.getItem('workspaceId');

    // Use existing HttpClient which already handles Authorization headers via interceptors
    const response = await HttpClient.apiClient.get('/ably/agentToken');

    if (!response.data) {
      throw new Error('Invalid token response from server');
    }

    const tokenData = response.data;

    // Create Ably client with token request from server
    ably = new Ably.Realtime({
      authCallback: (tokenRequest, callback) => {
        callback(null, tokenData);
      },
      echoMessages: false
    });

    // Wait for connection to be established
    await new Promise((resolve, reject) => {
      const connectionTimeout = setTimeout(() => {
        reject(new Error('Connection timeout after 10 seconds'));
      }, 10000);

      ably.connection.once('connected', () => {
        clearTimeout(connectionTimeout);
        console.log('Ably connection established successfully');
        resolve(true);
      });

      ably.connection.once('failed', (err: any) => {
        clearTimeout(connectionTimeout);
        reject(new Error(`Ably connection failed: ${err?.message || 'Unknown error'}`));
      });
    });

    return ably;
  } catch (error) {
    console.error('Error initializing Ably:', error);
    ably = null; // Reset the instance on error
    throw error;
  }
};

/**
 * Get an Ably channel with the specified name
 */
export const getAblyChannel = async (channelName: string) => {
  const ablyInstance = await initAbly();
  return ablyInstance.channels.get(channelName);
};

/**
 * Get or initialize an Ably Chat client
 */
export const getAblyChatClient = async () => {
  if (chatInstance) return chatInstance;

  const ablyInstance = await initAbly();
  chatInstance = new ChatClient(ablyInstance, {
    logLevel: LogLevel.Error
  });
  return chatInstance;
};

/**
 * Get an Ably Chat room with the specified ID and options
 */
export const getAblyChatRoom = async (roomId: string, options = AllFeaturesEnabled) => {
  const chatClient = await getAblyChatClient();
  return chatClient.rooms.get(roomId, options);
};

//create and subscribe to a channel with clientId
export const createAndSubscribeToChannel = async (userId: string, clientId: string, dispatch: any, notifications: NotificationState) => {
  // Clean up existing subscriptions first
  activeSubscriptions.forEach(({ subscription }) => {
    try {
      subscription.unsubscribe();
    } catch (error) {
      console.warn('Error unsubscribing from previous Ably channel:', error);
    }
  });
  activeSubscriptions = [];

  const ablyInstance = await initAbly();
  console.log('userIdASJHJHSJAHJSA', userId, clientId);

  //get new ticket
  const newTicketChannel = ablyInstance.channels.get(`notifications:client:${clientId}`);
  const newTicketSubscription = newTicketChannel.subscribe("new_ticket", (message) => {
    const ticket = message.data;
    dispatch(addNewTicket(ticket));
    dispatch(addNewTicketToViews({ ticket, currentUserId: userId }));
  });
  activeSubscriptions.push({ channel: `notifications:client:${clientId}`, subscription: newTicketSubscription });
  const channel = ablyInstance.channels.get(`notifications:user:${userId}`);
  const userTicketSubscription = channel.subscribe("new_ticket", (message) => {
    if (message.data.payload.routingType === "manual") {
      if (!notifications.notifications.unassigned.find((notification) => notification.id === message.data.notificationId)) {
        dispatch(clearTickets('all'));
        dispatch(fetchTickets());
        dispatch(fetchUnassignedTickets({ userId }));
        dispatch(setUnassignedData({ id: message.data.notificationId, type: message.name, payload: message.data.payload, entity_id: message.data.entityId, is_read: false, created_at: new Date().toISOString() }));
      }
    }
    if (message.data.payload.routingType === "load_balanced" || message.data.payload.routingType === "round_robin") {
      if (!notifications.notifications.inbox.find((notification) => notification.id === message.data.notificationId)) {
        dispatch(setInboxData({ id: message.data.notificationId, type: message.name, payload: message.data.payload, entity_id: message.data.entityId, is_read: false, created_at: new Date().toISOString() }));
      }
    }
    // dispatch(setInboxCount());  

    showNotification(message.data);
  });
  activeSubscriptions.push({ channel: `notifications:user:${userId}`, subscription: userTicketSubscription });

  // channel.subscribe("ticket_mention", (message) => {
  //   console.log('User ticket mention:', message);
  //   dispatch(setMentionsCount());
  //   showNotification(message.data);
  // });

  const userMessageSubscription = channel.subscribe("new_message", (message) => {
    console.log('User new message:', message);
    if (message.data.payload.routingType === "new_response") {
      dispatch(setNewResponseTicket(message.data.entityId));
    }
  });
  activeSubscriptions.push({ channel: `notifications:user:${userId}`, subscription: userMessageSubscription });

  // subscribe to channel for all events
  const orgChannel = ablyInstance.channels.get(`notifications:org:${clientId}:unassigned`);
  const orgTicketSubscription = orgChannel.subscribe("new_ticket", (message) => {
    console.log('Org new ticket:', message);
    dispatch(setUnassignedCount());
    if (!notifications.notifications.unassigned.find((notification) => notification.id === message.data.notificationId)) {
      dispatch(setUnassignedData({ id: message.data.notificationId, type: message.name, payload: message.data.payload, entity_id: message.data.entityId, is_read: false, created_at: new Date().toISOString() }));
    }
    showNotification(message.data);
  });
  activeSubscriptions.push({ channel: `notifications:org:${clientId}:unassigned`, subscription: orgTicketSubscription });
};
